import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VideocamIcon from '@mui/icons-material/Videocam';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import Footer from '../../components/Footer';
import { MarketingPage, Hero, Ticker, SectionHead, Faq, FinalCta, Cta } from '../../components/marketing/Blocks';
import { WorkshopHeroVisual } from '../../components/marketing/Visuals';
import { upcomingWorkshops } from '../../data/workshops';
import { audiences } from '../../data/audiences';
import { formatDate } from '../../data/liveClasses';
import { API_URL } from '../../config/api';
import { normalizePhone, PHONE_ERROR, PHONE_ALLOWED } from '../../utils/phone';
import '../LiveClasses/index.css';
import './index.css';

const accent = ['#fb923c', '#ec4899'];

const perks = [
  { title: 'Free. Actually free.', text: 'No fees, no card details, no catch. Register and show up.' },
  { title: 'Build, don’t just watch', text: 'Code along with an engineer and leave with something that works.' },
  { title: 'Ask anything, live', text: 'Your questions answered by the person teaching, in real time.' }
];

const faqs = [
  { q: 'Are the workshops really free?', a: 'Yes. Workshops are free to attend. Register for the one you want and we will confirm your seat.' },
  { q: 'How do I join?', a: 'Workshops are live and online. After you register, our team confirms your seat and shares the joining link before the session.' },
  { q: 'What if a date is not announced yet?', a: 'Register your interest anyway. We will contact you as soon as the date is fixed.' },
  { q: 'Do I need to prepare anything?', a: 'A laptop and a stable internet connection. Any setup needed for a hands-on workshop will be shared in advance.' }
];

const experienceOptions = ['Student', 'Fresher (0–1 years)', 'Working professional (1–3 years)', 'Working professional (3+ years)'];

const emptyForm = { name: '', emailAddress: '', phoneNumber: '', workshopId: '', experience: '' };

const whenLabel = (w) => (w.date ? `${formatDate(w.date)}${w.time ? ` · ${w.time}` : ''}` : 'Date announced soon');

// Free workshops at /workshops; the list lives in data/workshops.js
const Workshops = () => {
  const list = useMemo(upcomingWorkshops, []);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [serverError, setServerError] = useState('');
  const formRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    document.title = 'Free live workshops | Codeazy';
    return () => { document.title = 'Codeazy'; };
  }, []);

  // Deep links: /workshops?workshop=dsa-patterns#register
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('workshop');
    if (id && list.some((w) => w.id === id)) setForm((prev) => ({ ...prev, workshopId: id }));
    if (location.hash === '#register') {
      const timer = setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.search, location.hash, list]);

  const choose = (id) => {
    setForm((prev) => ({ ...prev, workshopId: id }));
    setErrors((prev) => ({ ...prev, workshopId: '' }));
    setStatus('idle');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Name is required';
    if (!form.emailAddress.trim()) next.emailAddress = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailAddress)) next.emailAddress = 'Please enter a valid email address';
    if (!form.phoneNumber.trim()) next.phoneNumber = 'Phone number is required';
    else if (!normalizePhone(form.phoneNumber)) next.phoneNumber = PHONE_ERROR;
    if (!form.workshopId) next.workshopId = 'Please choose a workshop';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const workshop = list.find((w) => w.id === form.workshopId);

    setStatus('submitting');
    setServerError('');
    try {
      // Stored as a live class request so it shows up in the admin dashboard
      const response = await fetch(`${API_URL}/class-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          emailAddress: form.emailAddress,
          phoneNumber: form.phoneNumber,
          course: `Workshop: ${workshop.title}`,
          batch: whenLabel(workshop),
          experience: form.experience,
          message: 'Free workshop registration'
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Something went wrong');
      setStatus('success');
      setForm(emptyForm);
    } catch (err) {
      console.error('Error registering for workshop:', err);
      setServerError(err.message === 'Failed to fetch' ? 'Could not reach the server. Please try again.' : err.message);
      setStatus('error');
    }
  };

  return (
    <MarketingPage accent={accent} sticky={{ text: 'Free live workshops', cta: { label: 'Register free', to: '#register' } }}>
      <Hero
        kicker="Free live workshops"
        title="Learn something real."
        highlight="In one evening. For free."
        text="Short, hands-on workshops with engineers. Build with AI tools, crack DSA patterns, walk through a real system design round, and leave with skills you can use the next day."
        ctas={[
          { label: 'See upcoming workshops', to: '#upcoming', primary: true },
          { label: 'Register for free', to: '#register' }
        ]}
        proof={['100% free', 'Live online', 'Hands-on', 'Live Q&A']}
        visual={<WorkshopHeroVisual />}
      />
      <Ticker items={list.map((w) => w.title)} />

      <section className="mkt-section">
        <div className="container">
          <div className="ws-perks">
            {perks.map((p, i) => (
              <div className="ws-perk" key={p.title} data-reveal style={{ '--i': i }}>
                <span className="ws-perk-num">0{i + 1}</span>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mkt-section" id="upcoming">
        <div className="container">
          <SectionHead kicker="Upcoming workshops" title="Pick your workshop" text="Every session is live, hands-on and free. Seats are confirmed in order of registration." />
          <div className="mkt-posters">
            {list.map((w, i) => {
              const Icon = w.icon;
              return (
                <article className="mkt-poster" key={w.id} data-reveal style={{ '--i': i % 3, '--c1': w.accent[0], '--c2': w.accent[1] }}>
                  <div className="mkt-poster-top">
                    <span className="mkt-poster-icon"><Icon /></span>
                    <span className="mkt-badge is-free">Free</span>
                  </div>
                  <h3>{w.title}</h3>
                  <p>{w.tagline}</p>
                  <ul className="mkt-poster-list">
                    {w.learn.map((item) => <li key={item}><CheckCircleIcon sx={{ fontSize: 16 }} /> {item}</li>)}
                  </ul>
                  <div className="mkt-poster-meta">
                    <span className={w.date ? '' : 'is-soon'}><CalendarMonthIcon sx={{ fontSize: 16 }} /> {whenLabel(w)}</span>
                    <span><ScheduleIcon sx={{ fontSize: 16 }} /> {w.duration}</span>
                    <span><SignalCellularAltIcon sx={{ fontSize: 16 }} /> {w.level}</span>
                    <span><VideocamIcon sx={{ fontSize: 16 }} /> Live online</span>
                  </div>
                  <Cta label={w.date ? 'Reserve my seat' : 'Register interest'} onClick={() => choose(w.id)} primary={Boolean(w.date)} />
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mkt-section" id="register" ref={formRef}>
        <div className="container">
          <div className="mkt-form-wrap" data-reveal>
            <div className="mkt-form-copy">
              <span className="mkt-eyebrow">Register for free</span>
              <h2 className="mkt-h2">Save your seat</h2>
              <p>Pick a workshop and leave your details. We will confirm your seat and share the joining link before the session.</p>
              <ul className="mkt-bullets">
                <li><CheckCircleRoundedIcon sx={{ fontSize: 18 }} /> Completely free</li>
                <li><CheckCircleRoundedIcon sx={{ fontSize: 18 }} /> Live and online</li>
                <li><CheckCircleRoundedIcon sx={{ fontSize: 18 }} /> Questions answered live</li>
              </ul>
            </div>

            {status === 'success' ? (
              <div className="live-form-success ws-success" role="status">
                <CheckCircleIcon sx={{ fontSize: 56 }} />
                <h3>You&apos;re in!</h3>
                <p>Thanks for registering. Our team will contact you with the details.</p>
                <button type="button" className="mkt-btn mkt-btn-ghost" onClick={() => setStatus('idle')}>Register for another workshop</button>
              </div>
            ) : (
              <form className="live-form ws-form" onSubmit={handleSubmit} noValidate>
                <div className="field">
                  <label htmlFor="ws-workshop">Workshop *</label>
                  <select id="ws-workshop" name="workshopId" value={form.workshopId} onChange={handleChange} className={errors.workshopId ? 'invalid' : ''}>
                    <option value="">Select a workshop</option>
                    {list.map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
                  </select>
                  {errors.workshopId && <span className="field-error">{errors.workshopId}</span>}
                </div>
                <div className="field">
                  <label htmlFor="ws-name">Full name *</label>
                  <input id="ws-name" name="name" value={form.name} onChange={handleChange} placeholder="Your name" autoComplete="name" className={errors.name ? 'invalid' : ''} />
                  {errors.name && <span className="field-error">{errors.name}</span>}
                </div>
                <div className="field-row">
                  <div className="field">
                    <label htmlFor="ws-email">Email *</label>
                    <input id="ws-email" type="email" name="emailAddress" value={form.emailAddress} onChange={handleChange} placeholder="you@example.com" autoComplete="email" className={errors.emailAddress ? 'invalid' : ''} />
                    {errors.emailAddress && <span className="field-error">{errors.emailAddress}</span>}
                  </div>
                  <div className="field">
                    <label htmlFor="ws-phone">Phone *</label>
                    <input
                      id="ws-phone"
                      type="tel"
                      name="phoneNumber"
                      value={form.phoneNumber}
                      onChange={(e) => PHONE_ALLOWED.test(e.target.value) && handleChange(e)}
                      placeholder="+91 98765 43210"
                      inputMode="tel"
                      autoComplete="tel"
                      maxLength={20}
                      className={errors.phoneNumber ? 'invalid' : ''}
                    />
                    {errors.phoneNumber && <span className="field-error">{errors.phoneNumber}</span>}
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="ws-experience">Where are you now?</label>
                  <select id="ws-experience" name="experience" value={form.experience} onChange={handleChange}>
                    <option value="">Select one</option>
                    {experienceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                {status === 'error' && <div className="form-alert" role="alert">{serverError}</div>}
                <button type="submit" className="mkt-btn mkt-btn-primary" disabled={status === 'submitting'}>
                  {status === 'submitting' ? 'Registering…' : 'Register for free'}
                  {status !== 'submitting' && <ArrowForwardIcon sx={{ fontSize: 19 }} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Faq items={faqs} />

      <FinalCta
        title="One evening can change your direction."
        text="Workshops are the start. For a structured path, join a live class or talk to a senior engineer about your plan."
        ctas={[
          { label: 'Explore live classes', to: '/live-classes', primary: true },
          { label: 'Talk to an expert', to: '/#book-session' }
        ]}
      >
        <nav className="mkt-others" aria-label="Programmes for you">
          <span>Programmes for you:</span>
          {audiences.map((a) => (
            <Link to={`/${a.slug}`} key={a.slug}>{a.navLabel} <ArrowForwardIcon sx={{ fontSize: 15 }} /></Link>
          ))}
        </nav>
      </FinalCta>

      <Footer />
    </MarketingPage>
  );
};

export default Workshops;
